import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Send, MessageCircle, Store, Clock } from "lucide-react";
import API, { formatError, getImageUrl } from "../api";
import { useAuth } from "../contexts/AuthContext";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "À l'instant";
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

export default function MessagesPage() {
  const { user } = useAuth();
  const [convos, setConvos] = useState([]);
  const [selectedConvo, setSelectedConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const bottomRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    API.get("/api/messages/conversations")
      .then((r) => setConvos(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const loadMessages = async (otherId) => {
    try {
      const { data } = await API.get(`/api/messages/${otherId}`);
      setMessages(data || []);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
      // Refresh convos to reset unread
      API.get("/api/messages/conversations").then((r) => setConvos(r.data || [])).catch(() => {});
    } catch {}
  };

  const selectConvo = (c) => {
    setSelectedConvo(c);
    loadMessages(c.other_user_id);
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => loadMessages(c.other_user_id), 5000);
  };

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!draft.trim() || !selectedConvo) return;
    setSending(true);
    setError("");
    try {
      const { data } = await API.post("/api/messages", {
        receiver_id: selectedConvo.other_user_id,
        content: draft.trim()
      });
      setMessages((prev) => [...prev, data]);
      setDraft("");
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setSending(false);
    }
  };

  if (!user) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
      <p className="text-gray-500 text-sm">Connectez-vous pour accéder à vos messages.</p>
    </div>
  );

  return (
    <div className="bg-[#F9FAFB] min-h-screen pb-24 md:pb-0">
      <div className="max-w-5xl mx-auto h-screen md:h-[calc(100vh-64px)] flex">

        {/* Sidebar — liste des conversations */}
        <div className={`w-full md:w-80 border-r border-gray-200 bg-white flex flex-col ${selectedConvo ? "hidden md:flex" : "flex"}`}>
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <Link to="/" className="text-gray-400 hover:text-gray-600">
                <ArrowLeft size={18} />
              </Link>
              <h1 className="font-extrabold text-[#232F3E]">Messages</h1>
            </div>
          </div>

          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#FF9900] border-t-transparent" />
            </div>
          ) : convos.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
              <MessageCircle size={40} className="text-gray-300 mb-3" />
              <p className="text-gray-500 text-sm font-medium">Aucun message</p>
              <p className="text-gray-400 text-xs mt-1">Vos conversations avec les vendeurs apparaîtront ici</p>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto">
              {convos.map((c) => (
                <button
                  key={c.other_user_id}
                  onClick={() => selectConvo(c)}
                  className={`w-full flex items-center gap-3 p-4 text-left border-b border-gray-50 hover:bg-gray-50 transition-colors ${selectedConvo?.other_user_id === c.other_user_id ? "bg-green-50" : ""}`}
                  data-testid={`convo-${c.other_user_id}`}
                >
                  <div className="w-10 h-10 rounded-full bg-[#FF9900] flex items-center justify-center shrink-0 text-white font-bold text-sm overflow-hidden">
                    {c.avatar ? (
                      <img src={getImageUrl(c.avatar)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      (c.shop_name || c.other_user_name || "?")[0].toUpperCase()
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="font-semibold text-sm text-[#232F3E] truncate">
                        {c.shop_name || c.other_user_name}
                      </p>
                      <span className="text-xs text-gray-400 shrink-0 ml-2">{timeAgo(c.last_message_time)}</span>
                    </div>
                    <p className="text-xs text-gray-500 truncate mt-0.5">{c.last_message}</p>
                  </div>
                  {c.unread_count > 0 && (
                    <span className="bg-[#25D366] text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shrink-0">
                      {c.unread_count > 9 ? "9+" : c.unread_count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Zone de chat */}
        {selectedConvo ? (
          <div className="flex-1 flex flex-col bg-white">
            {/* Chat header */}
            <div className="p-4 border-b border-gray-100 flex items-center gap-3">
              <button onClick={() => setSelectedConvo(null)} className="md:hidden text-gray-400 hover:text-gray-600">
                <ArrowLeft size={18} />
              </button>
              <div className="w-9 h-9 rounded-full bg-[#FF9900] flex items-center justify-center text-white font-bold text-sm overflow-hidden shrink-0">
                {selectedConvo.avatar ? (
                  <img src={getImageUrl(selectedConvo.avatar)} alt="" className="w-full h-full object-cover" />
                ) : (
                  (selectedConvo.shop_name || selectedConvo.other_user_name || "?")[0].toUpperCase()
                )}
              </div>
              <div>
                <p className="font-semibold text-sm text-[#232F3E]">{selectedConvo.shop_name || selectedConvo.other_user_name}</p>
                {selectedConvo.shop_name && (
                  <Link to={`/boutique/${selectedConvo.other_user_id}`} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                    <Store size={10} /> Voir la boutique
                  </Link>
                )}
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((m) => {
                const isMe = m.sender_id === user._id;
                return (
                  <div key={m._id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${isMe ? "bg-[#232F3E] text-white rounded-br-sm" : "bg-gray-100 text-gray-800 rounded-bl-sm"}`}>
                      <p className="leading-relaxed">{m.content}</p>
                      <p className={`text-[10px] mt-1 ${isMe ? "text-gray-400" : "text-gray-400"} text-right`}>
                        {timeAgo(m.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>

            {/* Input */}
            <form onSubmit={sendMessage} className="p-4 border-t border-gray-100 flex items-center gap-2">
              <input
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Écrire un message..."
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-full text-sm focus:ring-2 focus:ring-[#25D366] focus:border-transparent outline-none"
                data-testid="message-input"
              />
              <button
                type="submit"
                disabled={sending || !draft.trim()}
                className="w-10 h-10 bg-[#25D366] text-white rounded-full flex items-center justify-center hover:bg-[#20BD5A] transition-colors disabled:opacity-50 shrink-0"
                data-testid="send-message-btn"
              >
                <Send size={16} />
              </button>
            </form>
            {error && <p className="text-red-500 text-xs px-4 pb-2">{error}</p>}
          </div>
        ) : (
          <div className="flex-1 hidden md:flex items-center justify-center bg-gray-50 text-center px-8">
            <div>
              <MessageCircle size={48} className="text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 font-medium">Sélectionnez une conversation</p>
              <p className="text-gray-400 text-sm mt-1">Vos messages avec les vendeurs</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
